import {
  useQuery, UseQueryResult, useInfiniteQuery, UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { getTrips } from '../../services/TripsService';
import { TripsResponse, TripDetails } from '../../types/Trips';
import { FeaturesManagement } from '../../../featuresControl';
import { withShowStandaloneAndMergedTrips } from '../../../utils/queryFilters';
import useGetTripMerge from './useGetTripMerge';

function useGetTripStats(fleetId: string, query: string, enabled = true): UseQueryResult<TripsResponse> {
  const { data: tripMerge, isFetched } = useGetTripMerge(fleetId, enabled);
  const isReady = !enabled || isFetched;
  const fullQuery = withShowStandaloneAndMergedTrips(query, !!tripMerge?.clientTripMergeEnabled);

  return useQuery(
    ['trips', fleetId, fullQuery],
    async (): Promise<TripsResponse> => getTrips(fleetId, fullQuery),
    {
      retry: 1,
      retryDelay: 3000,
      refetchInterval: FeaturesManagement.productivity.refetchInterval,
      keepPreviousData: true,
      enabled: enabled && isReady,
    },
  );
}

export function useGetTripStatsLazy(fleetId: string, payload?: any, query?: string, deps?: any): UseInfiniteQueryResult<TripsResponse> {
  const { data: tripMerge, isFetched: isReady } = useGetTripMerge(fleetId);
  const showStandaloneAndMergedTrips = !!tripMerge?.clientTripMergeEnabled;
  const fullQuery = withShowStandaloneAndMergedTrips(query || '', showStandaloneAndMergedTrips);

  const infiniteQuery = useInfiniteQuery<any>(
    ['trips-lazy', fleetId, deps, fullQuery],
    async ({ pageParam }): Promise<TripsResponse> => {
      const rawQuery = typeof pageParam === 'string' && pageParam ? pageParam : fullQuery;
      const pageQuery = withShowStandaloneAndMergedTrips(rawQuery, showStandaloneAndMergedTrips);
      return getTrips(fleetId, pageQuery.replace(/(limit=)\d+/i, '$11000'));
    },
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: isReady,
      refetchInterval: FeaturesManagement.productivity.refetchIntervalLazyLists,
      getNextPageParam: () => undefined,
      select: (data) => {
        const hashMap = {} as Record<string, TripDetails>;
        const { entityKey } = payload;

        data.pages.forEach((page) => {
          page.results.forEach((item: TripDetails) => {
            const itemAsAny = item as any;
            const key: string = itemAsAny[entityKey] ?? 'unassigned';
            const currentEntity = hashMap[key];
            if (currentEntity) {
              hashMap[key] = {
                ...currentEntity,
                distanceInKm: currentEntity.distanceInKm + item.distanceInKm,
                alertCount: currentEntity.alertCount + item.alertCount,
                durationInMs: currentEntity.durationInMs + item.durationInMs,
              };
            } else {
              hashMap[key] = { ...item, [entityKey]: itemAsAny[entityKey] ?? null };
            }
          });
        });
        const results = Object.values(hashMap);

        return {
          ...data,
          pages: results,
        };
      },
    },
  );

  return {
    ...infiniteQuery,
    isLoading: !isReady || infiniteQuery.isLoading,
    isFetching: !isReady || infiniteQuery.isFetching,
  } as UseInfiniteQueryResult<TripsResponse>;
}

export default useGetTripStats;
